/**
 * CetusEd Community Portal — Authentication Provider
 *
 * COMPLIANCE: FERPA §1232g(b) requires authentication before accessing any
 *   student PII. The community portal uses portal code (student ID + access
 *   code) or magic link (email-based) authentication. Both methods produce
 *   a short-lived JWT that the API client attaches to all requests.
 *
 *   Portal codes are distributed by the school and can be revoked by staff.
 *   Magic links expire after 15 minutes (per NIST SP 800-63B §5.1.4.2).
 *
 * MIGRATION TARGET: The monolith S10 authenticates via OIDC for staff only.
 *   This provider implements a parent/guardian auth flow for the new portal.
 */

import React, { createContext, useCallback, useMemo, useReducer } from 'react';
import { AppError } from '@cetused/shared';
import { setAuthToken } from '../api/client';

export const AUTH_TOKEN_KEY = '_COMMUNITY_AUTH_TOKEN';

interface AuthState {
  token: string | null;
  user: CommunityUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

export interface CommunityUser {
  id: string;
  email: string;
  name: string;
  linkedStudents: Array<{
    id: string;
    name: string;
    school: string;
    relationship: string;
  }>;
}

type AuthAction =
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; token: string; user: CommunityUser }
  | { type: 'LOGIN_FAILURE' }
  | { type: 'LOGOUT' }
  | { type: 'RESTORE_TOKEN'; token: string; user: CommunityUser };

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'LOGIN_START':
      return { ...state, isLoading: true };
    case 'LOGIN_SUCCESS':
      return { token: action.token, user: action.user, isAuthenticated: true, isLoading: false };
    case 'LOGIN_FAILURE':
      return { token: null, user: null, isAuthenticated: false, isLoading: false };
    case 'LOGOUT':
      return { token: null, user: null, isAuthenticated: false, isLoading: false };
    case 'RESTORE_TOKEN':
      return { token: action.token, user: action.user, isAuthenticated: true, isLoading: false };
    default:
      return state;
  }
}

function loadPersistedAuth(): AuthState {
  try {
    const raw = sessionStorage.getItem(AUTH_TOKEN_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed.token && parsed.user) {
        setAuthToken(parsed.token);
        return { token: parsed.token, user: parsed.user, isAuthenticated: true, isLoading: false };
      }
    }
  } catch {
    sessionStorage.removeItem(AUTH_TOKEN_KEY);
  }
  return { token: null, user: null, isAuthenticated: false, isLoading: false };
}

interface AuthContextValue extends AuthState {
  loginWithPortalCode: (studentId: string, accessCode: string) => Promise<void>;
  loginWithMagicLink: (email: string) => Promise<void>;
  verifyMagicLink: (token: string) => Promise<void>;
  logout: () => void;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, undefined, loadPersistedAuth);

  const loginWithPortalCode = useCallback(async (studentId: string, accessCode: string) => {
    dispatch({ type: 'LOGIN_START' });
    try {
      const response = await fetch('/api/community/auth/portal-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ studentId, accessCode }),
      });
      const json = await response.json();
      if (!response.ok || !json.success) {
        throw AppError.unauthorized(json.error || 'Authentication failed');
      }
      const { token, user } = json.data;
      setAuthToken(token);
      sessionStorage.setItem(AUTH_TOKEN_KEY, JSON.stringify({ token, user }));
      dispatch({ type: 'LOGIN_SUCCESS', token, user });
    } catch (error) {
      dispatch({ type: 'LOGIN_FAILURE' });
      throw error;
    }
  }, []);

  const loginWithMagicLink = useCallback(async (email: string) => {
    const response = await fetch('/api/community/auth/magic-link', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    if (!response.ok) {
      throw AppError.internal('Failed to send magic link');
    }
  }, []);

  const verifyMagicLink = useCallback(async (token: string) => {
    dispatch({ type: 'LOGIN_START' });
    try {
      const response = await fetch('/api/community/auth/verify-magic-link', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      const json = await response.json();
      if (!response.ok || !json.success) {
        throw AppError.unauthorized(json.error || 'Invalid or expired magic link');
      }
      const { token: authToken, user } = json.data;
      setAuthToken(authToken);
      sessionStorage.setItem(AUTH_TOKEN_KEY, JSON.stringify({ token: authToken, user }));
      dispatch({ type: 'LOGIN_SUCCESS', token: authToken, user });
    } catch (error) {
      dispatch({ type: 'LOGIN_FAILURE' });
      throw error;
    }
  }, []);

  const logout = useCallback(() => {
    setAuthToken(null);
    sessionStorage.removeItem(AUTH_TOKEN_KEY);
    dispatch({ type: 'LOGOUT' });
  }, []);

  const value = useMemo(
    () => ({ ...state, loginWithPortalCode, loginWithMagicLink, verifyMagicLink, logout }),
    [state, loginWithPortalCode, loginWithMagicLink, verifyMagicLink, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
